/**
 * Shared configuration loading utilities
 * This module provides common functionality for loading configuration from files or environment variables
 */
import { logger } from "../../infrastructure/services/LoggerService";
/**
 * Loads API configuration based on environment
 * @returns Promise resolving to the API configuration object
 */
export async function loadApiConfig() {
    if (import.meta.env.PROD) {
        try {
            const configResponse = await fetch(import.meta.env.VITE_API_CONFIG_FILE);
            if (configResponse.ok) {
                const json = await configResponse.json();
                return {
                    baseUrl: json.apiBaseUrl,
                    bffUrl: json.apiBffUrl,
                };
            }
            else {
                logger.debug("Failed to fetch API config from config.json");
                throw new Error("API config not available");
            }
        }
        catch (error) {
            logger.debug("Error loading API config:", error);
            throw new Error("Failed to load API configuration");
        }
    }
    else {
        const baseUrl = import.meta.env.VITE_API_BASE_URL;
        const bffUrl = import.meta.env.VITE_API_BFF_URL;
        if (!baseUrl) {
            throw new Error("VITE_API_BASE_URL is not defined in development environment");
        }
        return {
            baseUrl,
            bffUrl,
        };
    }
}
/**
 * Loads API configuration based on environment
 * @returns Promise resolving to the API base URL
 * @deprecated Use loadApiConfig() instead
 */
export async function loadApiBaseUrl() {
    const config = await loadApiConfig();
    return config.baseUrl;
}
/**
 * Loads Adyen configuration based on environment
 * @returns Promise resolving to the Adyen configuration object
 */
export async function loadAdyenConfig() {
    if (import.meta.env.PROD) {
        try {
            const adyenConfigResponse = await fetch(import.meta.env.VITE_ADYEN_CONFIG_FILE);
            if (adyenConfigResponse.ok) {
                const adyenConfig = await adyenConfigResponse.json();
                return {
                    clientKey: adyenConfig.adyenClientKey,
                    environment: adyenConfig.adyenEnvironment,
                    merchantAccount: adyenConfig.adyenMerchantAccount,
                };
            }
            else {
                logger.debug("Failed to fetch Adyen config from config file");
                throw new Error("Adyen config not available");
            }
        }
        catch (error) {
            logger.debug("Error loading Adyen config:", error);
            throw new Error("Failed to load Adyen configuration");
        }
    }
    else {
        return {
            clientKey: import.meta.env.VITE_ADYEN_CLIENT_KEY,
            environment: import.meta.env.VITE_ADYEN_ENVIRONMENT,
            merchantAccount: import.meta.env.VITE_ADYEN_MERCHANT_ACCOUNT,
        };
    }
}
/**
 * Loads PostHog back office configuration based on environment
 * @returns Promise resolving to the PostHog configuration object
 */
export async function loadPostHogBOConfig() {
    if (import.meta.env.PROD) {
        try {
            const posthogConfigResponse = await fetch(import.meta.env.VITE_POSTHOG_BO_CONFIG_FILE);
            if (posthogConfigResponse.ok) {
                const posthogConfig = await posthogConfigResponse.json();
                return {
                    host: posthogConfig.posthogHost,
                    environment: posthogConfig.posthogEnvironment,
                    apiKey: posthogConfig.posthogApiKey,
                };
            }
            else {
                logger.debug("Failed to fetch PostHog back office config from config file");
                throw new Error("PostHog back office config not available");
            }
        }
        catch (error) {
            logger.debug("Error loading PostHog back office config:", error);
            throw new Error("Failed to load PostHog back office configuration");
        }
    }
    else {
        return {
            host: import.meta.env.VITE_POSTHOG_BO_HOST,
            environment: import.meta.env.VITE_POSTHOG_BO_ENVIRONMENT,
            apiKey: import.meta.env.VITE_POSTHOG_BO_API_KEY,
        };
    }
}
/**
 * Loads PostHog front office configuration based on environment
 * @returns Promise resolving to the PostHog configuration object
 */
export async function loadPostHogFOConfig() {
    if (import.meta.env.PROD) {
        try {
            const posthogConfigResponse = await fetch(import.meta.env.VITE_POSTHOG_FO_CONFIG_FILE);
            if (posthogConfigResponse.ok) {
                const posthogConfig = await posthogConfigResponse.json();
                return {
                    host: posthogConfig.posthogHost,
                    environment: posthogConfig.posthogEnvironment,
                    apiKey: posthogConfig.posthogApiKey,
                };
            }
            else {
                logger.debug("Failed to fetch PostHog front office config from config file");
                throw new Error("PostHog front office config not available");
            }
        }
        catch (error) {
            logger.debug("Error loading PostHog front office config:", error);
            throw new Error("Failed to load PostHog front office configuration");
        }
    }
    else {
        return {
            host: import.meta.env.VITE_POSTHOG_FO_HOST,
            environment: import.meta.env.VITE_POSTHOG_FO_ENVIRONMENT,
            apiKey: import.meta.env.VITE_POSTHOG_FO_API_KEY,
        };
    }
}
